import React, { useCallback, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';

import {
  useEditReimbursementVerification,
  useOtpSMSFlow,
  useReimbursementMethodDisclaimer,
  useReimbursementVerification,
} from '../../../hooks';

import checkIcon
  from '../../../assets/myAccount/reimbursement/check-payment-logo.svg';
import savingBankLogo
  from '../../../assets/myAccount/reimbursement/saving-bank-logo.svg';
import careCreditIcon
  from '../../../assets/myAccount/reimbursement/card-credit.svg';
import addIcon from '../../../assets/myPets/add-icon-blue.svg';

import {
  closeEditReimbursementMethodModal,
  openAddReimbursementMethodModal,
  sendVerificationCode,
} from '../../../actions';

import { ContactUsModal } from '../../sideMenu/contactUs/ContactUsModal';
import {
  ReimbursementMethodErrorModal,
  ReimbursementEditModal,
} from './editModal';
import { UpdateReimbursementModal } from './UpdateReimbursementModal';
import FigoLottie from '../../common/FigoLottie';

import { REIMBURSEMENT_METHODS } from '../../../constants';
import { CONTACT_US_STEPS } from '../../sideMenu/contactUs/contactUs.constants';
import { OTP_VERIFICATION_TYPE } from '../../common/otp/otp.constants';
import { MARKETING_CHANNEL_ID } from '../../../theme/theme.constants';

import { ClaimValidationModal, ModalContainer } from '../../common';
import { ModalOtpVerification } from '../../common/otp/ModalOtpVerification';
import { getServiceNameByMarketChannelId } from '../../common/otp/otp.utils';
import { VerifyOtpReimbursementModal } from './VerifyOtpReimbursementModal';
import { DirectDepositBanner } from './directDepositBanner';

import styles from './css/ReimbursementMethodSection.module.css';

const ReimbursementMethodSection = ({
  isRedirectAvailable = false,
  marketChannelId,
  policyNumber,
  reimbursementOption,
  source,
}) => {
  const { t } = useTranslation('myAccount');
  const dispatch = useDispatch();
  const [showOtpConfirmation, setShowOtpConfirmation] = useState(false);
  const useReimbursementMethodNewDesign = useReimbursementMethodDisclaimer();
  const store = useSelector(({
    billingPayments,
    claims,
    otpVerification,
    personalInformation,
  }) => ({
    billingPayments,
    claims,
    otpVerification,
    personalInformation,
  }), shallowEqual);
  const {
    billingPayments: {
      addReimbursementMethodModalVisible,
      editReimbursementMethodModalVisible,
      loadingReimbursementMethod,
      reimbursementMethodUpdateError,
    },
    otpVerification: { verificationCode: {
      showModalOtp,
      verified,
      verificationToken,
    } },
    personalInformation: { userInfo },
  } = store;

  const editReimbursementVerificationFlg = useEditReimbursementVerification();

  const { claimsResults, loading } = store.claims;

  const useSmsValidation = useOtpSMSFlow();
  const useReimVerification = useReimbursementVerification();
  const [
    updateReimbursementModalVisible,
    setUpdateReimbursementModalVisible,
  ] = useState(false);
  const [contactUsVisible, setContactUsVisible] = useState(false);
  const [
    claimValidationModalVisible,
    setClaimValidationModalVisible,
  ] = useState(false);

  const isCheck = reimbursementOption.PreferredReimbursementMethod
    === REIMBURSEMENT_METHODS.check;

  function handleEditReimbursementCancel() {
    dispatch(closeEditReimbursementMethodModal());
  }

  const phoneNumber = useMemo(() => userInfo?.PhoneNumber
    || '', [userInfo]);

  const email = useMemo(() => userInfo?.LoginEmail
    || '', [userInfo]);

  const hideUpdateReimbursementModal = () => {
    setUpdateReimbursementModalVisible(false);
  };

  const onContactUsClick = () => {
    setContactUsVisible(true);
  };

  const onContactUsClose = () => {
    setContactUsVisible(false);
  };

  const onCloseClaimValidationModal = () => {
    setClaimValidationModalVisible(false);
  };

  const hasOpenClaims = useMemo(() => {
    if (!claimsResults.open || !claimsResults.open.items) {
      return false;
    }

    const openFigoClaims = claimsResults.open.items
      .filter((claim) => claim.MarketingChannelId
        === MARKETING_CHANNEL_ID.figo);

    return openFigoClaims && openFigoClaims.length > 0;
  }, [claimsResults]);

  const onCloseModalOtpConfirmation = () => setShowOtpConfirmation(false);

  const onVerifyModalOtp = () => {
    setShowOtpConfirmation(false);
    const verificationType = useSmsValidation
      ? OTP_VERIFICATION_TYPE.SMS : OTP_VERIFICATION_TYPE.EMAIL;
    dispatch(sendVerificationCode({
      email,
      phoneNumber,
      serviceName: getServiceNameByMarketChannelId(marketChannelId),
      verificationType,
    }));
  };

  const hideEmail = useMemo(() => {
    if (!email) {
      return '';
    }

    const [localPart, domain] = email.split('@');
    const hiddenLocalPart = localPart.length > 2
      ? `${localPart[0]}*****${localPart.slice(-1)}`
      : `${localPart[0]}*****`;

    return `${hiddenLocalPart}@${domain}`;
  }, [email]);

  const handleChangeReimbursementClick = useCallback(() => {
    if (editReimbursementVerificationFlg && hasOpenClaims) {
      setClaimValidationModalVisible(true);
      return;
    }

    const defaultReimbursementMethod = useReimbursementMethodNewDesign
      ? REIMBURSEMENT_METHODS.deposit : REIMBURSEMENT_METHODS.check;

    if (!useReimVerification) {
      dispatch(openAddReimbursementMethodModal(defaultReimbursementMethod));
      return;
    }

    // If already verified, open the modal directly
    if (verified && verificationToken) {
      dispatch(openAddReimbursementMethodModal(defaultReimbursementMethod));
      return;
    }

    // If SMS validation is required but no phone number
    if (useSmsValidation && !phoneNumber) {
      setUpdateReimbursementModalVisible(true);
      return;
    }

    setShowOtpConfirmation(true);
  }, [
    claimsResults,
    dispatch,
    editReimbursementVerificationFlg,
    email,
    hasOpenClaims,
    phoneNumber,
    source,
    t,
    useReimbursementMethodNewDesign,
    useReimVerification,
    useSmsValidation,
    verificationToken,
    verified,
  ]);

  const renderReimbursementCard = useCallback(() => {
    if (loadingReimbursementMethod) {
      return (
        <div className={styles.lottieContainer}>
          <FigoLottie />
        </div>
      );
    }

    if (reimbursementOption.PreferredReimbursementMethod
      === REIMBURSEMENT_METHODS.none) {
      return (
        <div>
          {marketChannelId === MARKETING_CHANNEL_ID.figo && (
            <DirectDepositBanner
              notMethodSet
              validPolicyStatus={isRedirectAvailable}
            />
          )}

          <button
            className={styles.addButton}
            onClick={handleChangeReimbursementClick}
            type="button"
          >
            <img alt="" src={addIcon} />

            <span>{t('reimbursementSection.addReimbursementMethod')}</span>
          </button>
        </div>
      );
    }

    const hasCareCredit = reimbursementOption.PreferredReimbursementMethod
      === REIMBURSEMENT_METHODS.careCredit;
    const isBackupCheck =
      reimbursementOption.Details.Type === REIMBURSEMENT_METHODS.check;
    const secondaryBackup = isBackupCheck
      ? 'reimbursementSection.careCreditOption.secondaryCheck'
      : 'reimbursementSection.careCreditOption.secondaryBank';

    const editButtonVisible = () => {
      if (useReimbursementMethodNewDesign) {
        return !isCheck;
      }

      return true;
    };

    return (
      <div className={styles.cardContainer}>
        <div className={styles.cardRow}>
          <div className={styles.cardLeftContent}>
            <div className={styles.reimbursementDetail}>
              {hasCareCredit ? (
                <div className={styles.cardCreditDetailsContainer}>
                  <img alt="" src={careCreditIcon} />

                  <div>
                    <p>
                      <Trans
                        components={{
                          bold: <strong />,
                        }}
                        i18nKey="reimbursementSection.careCreditOption.primary"
                        ns="myAccount"
                        values={{
                          ending:
                            reimbursementOption.CareCredit.LastFourDigits,
                        }}
                      />
                    </p>

                    <span>
                      <Trans
                        components={{ bold: <strong /> }}
                        i18nKey={secondaryBackup}
                        ns="myAccount"
                        values={{
                          ending: isBackupCheck
                            ? reimbursementOption.Details.Address
                            : reimbursementOption.Details.AccountNumberLastFour,
                        }}
                      />
                    </span>
                  </div>
                </div>
              ) : (
                <>
                  <img alt="" src={isCheck ? checkIcon : savingBankLogo} />

                  <p>
                    {isCheck ? t('reimbursementSection.checkMethod', {
                      addressLine1: reimbursementOption.Details.AddressLine1,
                      addressLine2: reimbursementOption.Details.AddressLine2,
                      stateId: reimbursementOption.Details.State,
                      zipCode: reimbursementOption.Details.Zipcode,
                    }) : t('reimbursementSection.bankAccount', {
                      accountNumberLastFour:
                        reimbursementOption.AccountNumberLastFour,
                    })}
                  </p>
                </>
              )}
            </div>
          </div>

          <div className={styles.actions}>
            {editButtonVisible() && (
              <button
                className={styles.editButton}
                disabled={loading}
                onClick={handleChangeReimbursementClick}
                type="button"
              >
                <span>{t('reimbursementSection.editOptions.title')}</span>
              </button>
            )}
          </div>
        </div>

        {isCheck && marketChannelId === MARKETING_CHANNEL_ID.figo && (
          <DirectDepositBanner
            onSetupClick={handleChangeReimbursementClick}
            validPolicyStatus={isRedirectAvailable}
          />
        )}
      </div>
    );
  }, [
    handleChangeReimbursementClick,
    isCheck,
    isRedirectAvailable,
    loading,
    loadingReimbursementMethod,
    marketChannelId,
    reimbursementOption,
    t,
    useReimbursementMethodNewDesign,
  ]);

  return (
    <div className={styles.sectionContainer}>
      <h1>{t('reimbursementSection.reimbursementMethod')}</h1>

      <p>{t('reimbursementSection.reimbursmentDescription')}</p>

      {renderReimbursementCard()}

      <ReimbursementEditModal
        isEditing={editReimbursementMethodModalVisible}
        isVisible={editReimbursementMethodModalVisible
          || addReimbursementMethodModalVisible}
        onCancel={handleEditReimbursementCancel}
        policyNumber={policyNumber}
        source={source}
      />

      {reimbursementMethodUpdateError && <ReimbursementMethodErrorModal />}

      <VerifyOtpReimbursementModal
        hideEmail={hideEmail}
        onCloseModalOtpConfirmation={onCloseModalOtpConfirmation}
        onVerifyModalOtp={onVerifyModalOtp}
        phoneNumber={phoneNumber}
        show={showOtpConfirmation}
        useSmsValidation={useSmsValidation}
      />

      {useReimVerification ? (
        <ModalContainer
          cancelable={false}
          show={showModalOtp && !verified}
        >
          <ModalOtpVerification />
        </ModalContainer>
      ) : null}

      <UpdateReimbursementModal
        onButtonClick={onContactUsClick}
        onClose={hideUpdateReimbursementModal}
        show={updateReimbursementModalVisible}
      />

      <ContactUsModal
        companyStep={CONTACT_US_STEPS.HOME}
        onClose={onContactUsClose}
        show={contactUsVisible}
      />

      <ClaimValidationModal
        onClose={onCloseClaimValidationModal}
        visible={claimValidationModalVisible}
      />
    </div>
  );
};

export { ReimbursementMethodSection };
